import FormControl from 'common/components/FormControl/FormControl';
import FormScrollError from 'common/components/FormScrollError/FormScrollError';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import arrayMutators from 'final-form-arrays';
import useRouter from 'common/hooks/use-router';
import EditHeader from 'common/components/EditHeader/EditHeader';
import SectionTitle from 'common/components/SectionTitle/SectionTitle';
import ToggleSwitch from 'common/components/ToggleSwitch/ToggleSwitch';
import HeaderButtonsEdit from 'common/components/EditHeader/HeaderButtonsEdit';
import fixFinalFormEmptyValues from 'utils/fixFinalFormEmptyValues';
import dayjs from 'dayjs';
import Icon from 'components/Icon/Icon';
import { Profile } from 'types/Profile';
import EstablishmentSelect from 'components/select/EstablishmentSelect/EstablishmentSelect';
import DepartmentSelect from 'components/select/DepartmentSelect/DepartmentSelect';
import { isMailCorrect, isPasswordCorrect } from 'common/components/Password/Password';
import Button from 'common/components/Button/Button';
import useItem from 'common/hooks/useItem';
import AlertsContext from 'common/providers/alerts';

function StudentForm({ onSubmit, editedUser, myAccount }: any) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const [changePassword, setChangePassword] = useState(false);

  const checkEmail = (value: string) => (isMailCorrect(value) ? undefined : t('forms.badEmail'));
  const checkPassword = (value: string) =>
    isPasswordCorrect(value) ? undefined : t('common.passwordTooWeak');

  const editedProfile = editedUser?.profiles?.[0];

  const { updateItem, error, isFetching }: any = useItem('users', editedUser?.id);

  useEffect(() => {
    if (error) {
      setAlert(error.message || t('common.error'), 'danger');
    }
  }, [error, setAlert, t]);

  const changePasswordHandler = useCallback(
    async (userData: any) => {
      const user = { ...userData };

      try {
        await updateItem(user.id, user, false);

        setChangePassword(false);
      } catch (e: any) {
        return e;
      }
      return true;
    },
    [updateItem]
  );

  const initialValues: Profile = useMemo(
    () =>
      editedProfile?.id
        ? {
            ...editedProfile,
            role: editedUser?.role?.id ?? 1,
            role_name: editedUser?.role?.name,
            establishment: editedProfile?.establishment?.id,
            department: editedProfile?.establishment?.department?.id,
            user: editedUser,
          }
        : {
            username: undefined,
            phone: '',
            description: '',
            blocked: false,
            confirmed: true,
            isMediator: false,
            verified: false,
            isValidStudent: false,
            role: editedUser?.role?.id,
            role_name: editedUser?.role?.name,
            user: editedUser,
          },
    [editedProfile, editedUser]
  );

  const onSubmitWrapper = async (values: any) => {
    delete values.department;
    const res = await onSubmit(fixFinalFormEmptyValues(initialValues, values));
    return res;
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{
        ...arrayMutators,
      }}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      render={({ handleSubmit, values }) => (
        <div className="mx-auto max-w-full py-6 px-6 text-slate-500 text-base">
          {/* Header */}
          <EditHeader
            title={
              myAccount
                ? `${t('titlePages.myAccount')}`
                : editedProfile && editedProfile.id
                ? t('editItem.studentProfile')
                : t('newItem.studentProfile')
            }
            subTitle={
              editedProfile?.id
                ? `${values.username} - ${values.firstname || ''} ${values.lastname || ''}`
                : t('newItem.studentProfileLegend')
            }
            icon="UsersIcon"
            HeaderButtonsEdit={
              <HeaderButtonsEdit
                inHeader
                onSubmit={handleSubmit}
                onCancel={myAccount ? null : () => navigate('/students')}
              />
            }
          />
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FormScrollError />

            <div className="responsive-flex gap-6 mt-2">
              <div className="md:w-2/3 flex flex-col gap-6">
                <div className="white-box">
                  <div className="responsive-flex gap-4">
                    <div className="flex flex-col w-full md:w-[180px] justify-center">
                      <Icon
                        name="UserIcon"
                        className="w-full text-center w-24 h-24 rounded-sm p-1 mr-0.5 text-slate-500"
                      />
                      <div className="mt-1 mb-4 flex items-center justify-center gap-1 leading-[0] text-slate-500 shrink-0 font-normal pb-1">
                        {values.isMediator ? t('roles.Mediator') : t('roles.Authenticated')}
                      </div>
                      {editedProfile?.id ? (
                        <div className="text-xs text-center w-full px-1 pt-0 pb-4">
                          {t('forms.subscribedThe', {
                            since: dayjs(values?.createdAt).format('DD/MM/YYYY'),
                          })}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-col w-full gap-2">
                      <FormControl name="username" titleKey="forms.username" required />
                      <FormControl name="firstname" titleKey="forms.firstname" required />
                      <FormControl name="lastname" titleKey="forms.lastname" required />
                      <FormControl name="phone" titleKey="forms.phone" required />
                    </div>
                  </div>
                </div>
                <div className="white-box flex flex-col gap-4">
                  <FormControl
                    title={t('forms.department')}
                    labelIcon="IoLocationOutline"
                    type="custom"
                    name="department"
                    inputContainerStyle="w-full"
                    className="w-full"
                    containerStyle="w-full"
                  >
                    <DepartmentSelect />
                  </FormControl>
                  <FormControl
                    title={t('forms.establishment')}
                    labelIcon="EstablishmentIcon"
                    type="custom"
                    name="establishment"
                    inputContainerStyle="w-full"
                    className="w-full"
                    containerStyle="w-full"
                    placeholder={!values?.department ? "Sélectionner d'abord un département" : ''}
                    disabled={!values?.department}
                  >
                    <EstablishmentSelect filterOn={{ department: values?.department }} />
                  </FormControl>

                  <FormControl
                    title={t('forms.training')}
                    labelIcon="Training"
                    type="text"
                    name="training"
                    inputContainerStyle="w-full"
                    className="w-full"
                    containerStyle="w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col md:w-1/3 gap-6">
                <div className="white-box">
                  <div className="flex flex-col w-full gap-2">
                    <SectionTitle title={t('forms.authentication')} icon="CalendarDaysIcon" />

                    <FormControl
                      name="user.email"
                      titleKey="forms.email"
                      required
                      validate={checkEmail}
                    />
                    <FormControl type="custom" name="user.confirmed" containerStyle="my-2">
                      <ToggleSwitch
                        rightLabel={t('forms.confirmed')}
                        disabled={values?.user?.confirmed}
                      />
                    </FormControl>
                    <FormControl type="custom" name="user.blocked" containerStyle="my-2">
                      <ToggleSwitch rightLabel={t('forms.blocked')} />
                    </FormControl>
                    <FormControl type="custom" name="isValidStudent" containerStyle="my-2">
                      <ToggleSwitch rightLabel={t('forms.validStudent')} />
                    </FormControl>
                  </div>
                </div>
                {!changePassword && values?.id && (
                  <div className="w-full flex flex-row justify-center gap-2">
                    <Button
                      type="primary-line"
                      onClick={() => {
                        values.user.password = '';
                        (values.user as any).confirmPassword = '';
                        setChangePassword(true);
                      }}
                      title={t('actions.changePassword')}
                      className="my-auto py-4"
                    />
                  </div>
                )}
                {changePassword && (
                  <div className="white-box flex flex-col gap-4">
                    <FormControl
                      type="jpassword"
                      name="user.password"
                      titleKey="forms.password"
                      required
                      validate={checkPassword}
                    />
                    <FormControl
                      name="user.confirmPassword"
                      title={t('forms.confirmPassword')}
                      type="password"
                    />

                    <div className="w-full flex flex-row justify-center gap-2">
                      <Button
                        type="neutral"
                        onClick={() => setChangePassword(false)}
                        title={t('actions.cancel')}
                        className="justify-center py-4"
                      />
                      <Button
                        type="primary"
                        onClick={() => changePasswordHandler(values?.user)}
                        title={t('actions.changePassword')}
                        className="justify-center py-4"
                        loading={isFetching}
                      />
                    </div>
                    {t('common.passwordInstruction')}
                  </div>
                )}
              </div>
            </div>

            <HeaderButtonsEdit
              onSubmit={handleSubmit}
              onCancel={myAccount ? null : () => navigate('/mediators')}
            />
          </form>
        </div>
      )}
    />
  );
}
export default StudentForm;
